import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { TrendingUp, TrendingDown, Wallet, Trash2, Plus, FileDown, BarChart3, RotateCcw } from "lucide-react";
import { generateFinanceReport } from "../../pdfReports";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
const brl = (n) => (n || 0).toLocaleString("pt-BR", { style:"currency", currency:"BRL" });

const EMPTY = { type:"income", amount:0, category:"Serviço", description:"", date: today() };

export default function FinanceTab() {
  const [month, setMonth] = useState(currentMonth());
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [apts, setApts] = useState([]);
  const [trash, setTrash] = useState([]);
  const [sel, setSel] = useState([]);

  const load = () => {
    api.get("/admin/finance", { params:{ month } }).then(r=>setEntries(r.data));
    api.get("/admin/finance/appointments", { params:{ month } }).then(r=>setApts(r.data)).catch(()=>{});
    api.get("/admin/finance/trash").then(r=>setTrash(r.data)).catch(()=>{});
    api.get("/admin/finance/summary", { params:{ month } }).then(r=>setSummary(r.data));
  };
  useEffect(() => { setSel([]); load(); }, [month]);
  useEffect(() => { api.get("/admin/finance/history").then(r=>setHistory(r.data)).catch(()=>{}); }, [entries.length]);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Informe um valor"); return; }
    try {
      await api.post("/admin/finance", { ...form, amount: Number(form.amount) });
      toast.success(form.type === "income" ? "Entrada registrada" : "Despesa registrada");
      setForm(EMPTY); load();
    } catch { toast.error("Falha ao salvar"); }
  };
  const del = async (id) => {
    if (!window.confirm("Excluir lançamento?")) return;
    await api.delete(`/admin/finance/${id}`); toast.success("Movido para a lixeira"); load();
  };
  const restore = async (id) => {
    await api.post(`/admin/finance/${id}/restore`); toast.success("Lançamento restaurado"); load();
  };
  const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSel = entries.length > 0 && sel.length === entries.length;
  const bulkDel = async (ids, msg) => {
    if (!ids.length || !window.confirm(msg)) return;
    const { data } = await api.post("/admin/finance/bulk-delete", ids);
    toast.success(`${data.deleted} lançamento(s) movido(s) para a lixeira`); setSel([]); load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="card-arya p-6 flex flex-wrap items-end gap-4 justify-between">
          <div>
            <label className="text-xs tracking-widest font-display text-[color:var(--sun-700)]">MÊS DE REFERÊNCIA</label>
            <input type="month" value={month} onChange={e=>setMonth(e.target.value)}
                   data-testid="finance-month"
                   className="block mt-1 rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm"/>
          </div>
          <button onClick={()=>generateFinanceReport({ month, summary, entries })}
                  className="btn-ghost inline-flex items-center gap-2" data-testid="btn-pdf-finance">
            <FileDown size={14}/> Baixar PDF do mês
          </button>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1 min-w-[280px]">
            <StatCard label="ENTRADAS SERVIÇOS" value={brl(summary?.appointments_income)} tone="sun" icon={<TrendingUp size={14}/>} />
            <StatCard label="OUTRAS ENTRADAS" value={brl(summary?.manual_income)} tone="sun" icon={<TrendingUp size={14}/>} />
            <StatCard label="DESPESAS" value={brl(summary?.expense)} tone="red" icon={<TrendingDown size={14}/>} />
            <StatCard label="LUCRO" value={brl(summary?.profit)} tone="green" icon={<Wallet size={14}/>} />
          </div>
        </div>

        <AllTimeCard refreshKey={`${month}-${entries.length}-${trash.length}-${summary?.total_income}`} />
        <div className="card-arya p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={16} className="text-[color:var(--sun-600)]"/>
            <h3 className="font-serif text-xl">Últimos 6 meses</h3>
          </div>
          <div style={{ width:"100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFE6E1"/>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#75626D" }}/>
                <YAxis tick={{ fontSize: 11, fill: "#75626D" }}/>
                <Tooltip formatter={(v)=>brl(v)} contentStyle={{ borderRadius: 12, border:"1px solid #EFE6E1" }}/>
                <Legend wrapperStyle={{ fontSize: 12 }}/>
                <Bar dataKey="income" name="Entradas" fill="#C79A3A" radius={[6,6,0,0]}/>
                <Bar dataKey="expense" name="Despesas" fill="#B33A3A" radius={[6,6,0,0]}/>
                <Bar dataKey="profit" name="Lucro" fill="#4A7C3E" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-arya overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 p-4 border-b border-[var(--line)]" data-testid="finance-bulk-bar">
            <span className="text-xs text-[color:var(--ink-500)] flex-1">{sel.length} selecionado(s)</span>
            <button disabled={!sel.length} onClick={()=>bulkDel(sel, `Excluir ${sel.length} lançamento(s) selecionado(s)?`)} className="btn-ghost !py-1 !px-3 text-xs disabled:opacity-40" data-testid="btn-del-selected">Excluir selecionados</button>
            <button disabled={!entries.length} onClick={()=>bulkDel(entries.map(e=>e.entry_id), `Limpar TODOS os ${entries.length} lançamentos manuais de ${month}? Eles ficam na lixeira e podem ser restaurados.`)} className="btn-ghost !py-1 !px-3 text-xs !text-[color:var(--danger)] disabled:opacity-40" data-testid="btn-clear-month">Limpar mês</button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[var(--cream-100)] text-left text-xs tracking-widest font-display">
              <tr><th className="p-4 w-8"><input type="checkbox" checked={allSel} onChange={()=>setSel(allSel ? [] : entries.map(e=>e.entry_id))} data-testid="fin-select-all"/></th><th className="p-4">DATA</th><th className="p-4">TIPO</th><th className="p-4">CATEGORIA</th><th className="p-4">DESCRIÇÃO</th><th className="p-4 text-right">VALOR</th><th className="p-4"></th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {entries.map(e => (
                <tr key={e.entry_id} data-testid={`fin-${e.entry_id}`}>
                  <td className="p-4"><input type="checkbox" checked={sel.includes(e.entry_id)} onChange={()=>toggleSel(e.entry_id)} data-testid={`fin-check-${e.entry_id}`}/></td>
                  <td className="p-4">{e.date}</td>
                  <td className="p-4"><span className={`tag-sun ${e.type==='expense' ? '!text-[color:var(--danger)] !border-[color:var(--danger)]/40 !bg-[rgba(179,58,58,.08)]' : ''}`}>{e.type==='income' ? 'Entrada' : 'Despesa'}</span></td>
                  <td className="p-4">{e.category}</td>
                  <td className="p-4 text-[color:var(--ink-500)]">{e.description || '—'}</td>
                  <td className={`p-4 text-right font-display font-semibold ${e.type==='expense' ? 'text-[color:var(--danger)]' : 'text-[color:var(--success)]'}`}>
                    {e.type==='expense' ? '- ' : '+ '}{brl(e.amount)}
                  </td>
                  <td className="p-4"><button onClick={()=>del(e.entry_id)} className="p-1 text-[color:var(--danger)]" data-testid={`del-fin-${e.entry_id}`}><Trash2 size={16}/></button></td>
                </tr>
              ))}
              {apts.map(a => (
                <tr key={a.appointment_id} data-testid={`fin-apt-${a.appointment_id}`} className="bg-[rgba(233,204,133,.06)]">
                  <td className="p-4"></td>
                  <td className="p-4">{(a.start_at||"").slice(0,10)}</td>
                  <td className="p-4"><span className="tag-sun">Atendimento</span></td>
                  <td className="p-4">Agenda</td>
                  <td className="p-4 text-[color:var(--ink-500)]">{a.service_title} · {a.user_name}</td>
                  <td className="p-4 text-right font-display font-semibold text-[color:var(--success)]">+ {brl(a.price)}</td>
                  <td className="p-4"></td>
                </tr>
              ))}
              {entries.length===0 && apts.length===0 && <tr><td colSpan="7" className="p-8 text-center text-[color:var(--ink-500)]">Nenhum lançamento neste mês.</td></tr>}
            </tbody>
          </table>
        </div>
        {trash.length > 0 && <TrashList items={trash} onRestore={restore}/>}
      </div>

      <div className="card-arya p-6 h-fit">
        <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Plus size={16}/> Novo lançamento</h3>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={()=>setForm({...form, type:"income"})} aria-pressed={form.type==='income'}
                    className={`rounded-xl border py-2 text-sm ${form.type==='income'?'border-[color:var(--success)] bg-[rgba(74,124,62,.08)] text-[color:var(--success)]':'border-[var(--line)]'}`}
                    data-testid="fin-type-income">Entrada</button>
            <button onClick={()=>setForm({...form, type:"expense"})} aria-pressed={form.type==='expense'}
                    className={`rounded-xl border py-2 text-sm ${form.type==='expense'?'border-[color:var(--danger)] bg-[rgba(179,58,58,.08)] text-[color:var(--danger)]':'border-[var(--line)]'}`}
                    data-testid="fin-type-expense">Despesa</button>
          </div>
          <input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="fin-date"/>
          <input placeholder="Categoria (ex: Aluguel, Material, Extra)" value={form.category}
                 onChange={e=>setForm({...form, category:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="fin-category"/>
          <input type="number" step="0.01" placeholder="Valor (R$)" value={form.amount||""}
                 onChange={e=>setForm({...form, amount:e.target.value})}
                 className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="fin-amount"/>
          <textarea rows="2" placeholder="Descrição" value={form.description}
                    onChange={e=>setForm({...form, description:e.target.value})}
                    className="w-full rounded-xl border border-[var(--line)] bg-white px-3 py-2 text-sm" data-testid="fin-desc"/>
          <button onClick={save} className="btn-gold w-full" data-testid="btn-save-fin">Registrar</button>
          <p className="text-[10px] text-[color:var(--ink-500)]">Entradas de serviços concluídos são somadas automaticamente pela agenda.</p>
        </div>
      </div>
    </div>
  );
}

function TrashList({ items, onRestore }) {
  return (
    <div className="card-arya p-6" data-testid="finance-trash">
      <h3 className="font-serif text-xl mb-3 flex items-center gap-2"><Trash2 size={16}/> Lixeira</h3>
      <div className="divide-y divide-[var(--line)] text-sm">
        {items.map(e => (
          <div key={e.entry_id} className="flex items-center justify-between py-2 gap-3" data-testid={`trash-${e.entry_id}`}>
            <span>{e.date} · {e.category} · {e.description || "—"} · {e.type==='expense' ? '- ' : '+ '}{brl(e.amount)}</span>
            <button onClick={()=>onRestore(e.entry_id)} className="btn-ghost !py-1 !px-3 text-xs inline-flex items-center gap-1" data-testid={`restore-fin-${e.entry_id}`}><RotateCcw size={12}/> Restaurar</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AllTimeCard({ refreshKey }) {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/admin/finance/summary").then(r => setT(r.data)).catch(() => {}); }, [refreshKey]);
  return (
    <div className="card-arya p-5" data-testid="finance-all-time">
      <p className="text-xs tracking-widest font-display text-[color:var(--sun-700)] mb-3">ENTRADAS ATÉ HOJE · TODOS OS MESES</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="ENTRADAS ATÉ HOJE" value={brl(t?.total_income)} tone="sun" icon={<TrendingUp size={14}/>} />
        <StatCard label="SAÍDAS ATÉ HOJE" value={brl(t?.expense)} tone="red" icon={<TrendingDown size={14}/>} />
        <StatCard label="LUCRO ATÉ HOJE" value={brl(t?.profit)} tone="green" icon={<Wallet size={14}/>} />
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, icon }) {
  const toneClass = tone==='green' ? 'text-[color:var(--success)]'
                  : tone==='red' ? 'text-[color:var(--danger)]'
                  : 'text-[color:var(--sun-700)]';
  return (
    <div className="rounded-xl border border-[var(--line)] p-3 bg-white/60">
      <div className={`flex items-center gap-1 text-[10px] tracking-widest font-display ${toneClass}`}>{icon} {label}</div>
      <p className={`font-serif text-xl mt-1 ${toneClass}`}>{value}</p>
    </div>
  );
}
